import { TOKEN_KEY } from '../utils/constants';

export type SuggestionType = 'breathing' | 'grounding' | 'journaling' | 'reframe' | 'microAction';
export type CaffeineLevel = 'none' | 'low' | 'medium' | 'high';
export type SocialType = 'alone' | 'friends' | 'family' | 'work';
export type LocationType = 'home' | 'work' | 'outside' | 'commute';

export interface CheckInRecord {
  _id: string;
  userId: string;
  topic: string;
  createdAt: string;
  updatedAt?: string;
  localDate: string;
  moodScore: number;
  anxietyScore: number;
  energyScore?: number;
  emotionTags: string[];
  triggerTags: string[];
  notes: string;
  context: {
    sleepHours?: number;
    caffeine?: CaffeineLevel;
    social?: SocialType;
    locationType?: LocationType;
  };
  aiSummary?: string;
  aiSuggestion?: {
    type: SuggestionType;
    id: string;
    text: string;
  };
}

export interface CreateCheckInPayload {
  topic: string;
  localDate: string;
  moodScore: number;
  anxietyScore: number;
  energyScore?: number;
  emotionTags: string[];
  triggerTags: string[];
  notes: string;
  context: {
    sleepHours?: number;
    caffeine?: CaffeineLevel;
    social?: SocialType;
    locationType?: LocationType;
  };
}

interface CreateCheckInResponse {
  success: boolean;
  data: CheckInRecord;
  feedback?: {
    summary: string;
    suggestion: {
      type: SuggestionType;
      id: string;
      text: string;
    };
  };
  message?: string;
}

interface GetCheckInsResponse {
  success: boolean;
  data: CheckInRecord[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  message?: string;
}

interface WeeklyInsightsResponse {
  success: boolean;
  data: WeeklyInsights;
  message?: string;
}

export interface GetCheckInsParams {
  topic?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export interface WeeklyInsights {
  from: string;
  to: string;
  totalCheckIns: number;
  averageMood: number | null;
  averageAnxiety: number | null;
  topEmotionTags: string[];
  weekdayPattern: string;
  sleepCorrelation: string;
}

const getToken = (): string => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) {
    throw new Error('Unauthorized: No token found');
  }
  return token;
};

const parseErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  try {
    const data = await response.json();
    if (typeof data?.message === 'string' && data.message.trim()) {
      return data.message;
    }
  } catch (error) {
    console.error('Failed to parse API error payload:', error);
  }
  return fallback;
};

export const createCheckIn = async (
  payload: CreateCheckInPayload,
): Promise<{ checkIn: CheckInRecord; feedback?: CreateCheckInResponse['feedback'] }> => {
  const token = getToken();
  const response = await fetch(`${process.env.REACT_APP_API_URL}/api/checkins`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const message = await parseErrorMessage(response, `Failed to save check-in (${response.status})`);
    throw new Error(message);
  }

  const data: CreateCheckInResponse = await response.json();
  if (!data.success || !data.data) {
    throw new Error(data.message || 'Failed to save check-in');
  }

  return { checkIn: data.data, feedback: data.feedback };
};

export const getCheckIns = async (params: GetCheckInsParams = {}): Promise<CheckInRecord[]> => {
  const token = getToken();
  const query = new URLSearchParams();

  if (params.topic?.trim()) {
    query.append('topic', params.topic.trim());
  }
  if (params.from) {
    query.append('from', params.from);
  }
  if (params.to) {
    query.append('to', params.to);
  }
  if (params.page) {
    query.append('page', String(params.page));
  }
  if (params.limit) {
    query.append('limit', String(params.limit));
  }

  const queryString = query.toString();
  const url = `${process.env.REACT_APP_API_URL}/api/checkins${queryString ? `?${queryString}` : ''}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const message = await parseErrorMessage(response, `Failed to fetch check-ins (${response.status})`);
    throw new Error(message);
  }

  const data: GetCheckInsResponse = await response.json();
  if (!data.success || !Array.isArray(data.data)) {
    throw new Error(data.message || 'Failed to fetch check-ins');
  }

  return data.data;
};

export const getWeeklyInsights = async (end?: string, topic?: string): Promise<WeeklyInsights> => {
  const token = getToken();
  const query = new URLSearchParams();
  if (end) {
    query.append('end', end);
  }
  if (typeof topic === 'string' && topic.trim()) {
    query.append('topic', topic.trim());
  }

  const queryString = query.toString();
  const url = `${process.env.REACT_APP_API_URL}/api/checkins/weekly-insights${queryString ? `?${queryString}` : ''}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const message = await parseErrorMessage(response, `Failed to fetch weekly insights (${response.status})`);
    throw new Error(message);
  }

  const data: WeeklyInsightsResponse = await response.json();
  if (!data.success || !data.data) {
    throw new Error(data.message || 'Failed to fetch weekly insights');
  }

  return data.data;
};
